import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faSpinner,
  faUser,
  faEnvelope,
  faPhone,
  faGraduationCap,
  faPaperPlane,
  faCreditCard,
  faUniversity,
  faCheckCircle
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import ApiServices from "../Services/ApiServices";
import { toastHelper } from "../../Helpers/toastHelper";
import extractErrorMessage from "../../Helpers/APIHelper";

const validationSchema = Yup.object().shape({
  fullName: Yup.string()
    .required("الاسم مطلوب")
    .min(3, "الاسم يجب أن يكون 3 أحرف على الأقل")
    .max(50, "الاسم يجب أن لا يتجاوز 50 حرف"),
  email: Yup.string()
    .required("البريد الإلكتروني مطلوب")
    .email("البريد الإلكتروني غير صالح"),
  phone: Yup.string()
    .required("رقم الهاتف مطلوب")
    .matches(/^[0-9]{11}$/, "رقم الهاتف يجب أن يكون 11 أرقام")
    .test("is-valid-phone", "رقم الهاتف غير صالح", value => {
      if (!value) return false;
      return value.startsWith("01");
    }),
  paymentMethod: Yup.number()
    .required("طريقة الدفع مطلوبة")
    .min(1, "يرجى اختيار طريقة دفع صحيحة")
});

const ModalPopUp = ({
  isOpen,
  onClose,
  onConfirm,
  title = "العنوان",
  children,
  loading = false,
  confirmText = "تأكيد",
  cancelText = "إلغاء",
  showModalButtons = true,
  actions = null,
  mode = "default", // "default" or "register"
  planId = null,
  packageDetails = null
}) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (mode === "register" && isOpen) {
      fetchPaymentMethods();
    }
  }, [mode, isOpen]);

  const fetchPaymentMethods = async () => {
    try {
      setLoadingPaymentMethods(true);
      const response = await ApiServices.get('Lookup/PaymentMethods');
      setPaymentMethods(response);
    } catch (error) {
      
      toastHelper.error('فشل في تحميل طرق الدفع');
    } finally {
      setLoadingPaymentMethods(false);
    }
  };

  async function handleRegisterSubmit (values, { setSubmitting: setFormSubmitting })  {
    setSubmitting(true);
    try {
      const requestData = {
        full_name: values.fullName,
        email: values.email,
        phone: values.phone,
        plan_id: planId,
        payment_method: parseInt(values.paymentMethod)
      };

      const response = await ApiServices.post('Subscription/Request/create', requestData);

      if (response.check) {
        toastHelper.success('تم إرسال طلبك بنجاح، سيتم التواصل معك قريباً');
        onClose();
        if (onConfirm) onConfirm();
      } else {
        throw new Error(response.msg || 'فشل في إرسال الطلب');
      }
    } catch (error) {
      const title = error?.title || error?.msg || "";
      if (title.toLowerCase().includes("validation errors")) {
        const errMessage = extractErrorMessage(error);
        toastHelper.error(errMessage);
      } else {
        toastHelper.error(error.msg || 'حدث خطأ أثناء إرسال الطلب');
      }
    } finally {
      setSubmitting(false);
      setFormSubmitting(false);
    }
  };

  const getPaymentMethodIcon = (methodId) => {
    switch (methodId) {
      case 1:
        return (
          <div className="vodafone-icon">
            <svg width="24" height="24" viewBox="-0.398 -4.59 378.918 388.633" xmlns="http://www.w3.org/2000/svg">
              <path d="m119.441 14.328c47.465-18.918 102.754-17.61 148.954 4.363-13.165-2-26.555-.492-39.586 1.711-35.391 6.684-69.024 23.336-95.313 48.075-25.207 24.777-42.871 57.652-47.86 92.824-3.3 24.922-.241 51.062 10.942 73.746 11.524 23.8 31.809 43.32 56.258 53.394 23.559 9.97 50.887 9.86 74.75.961 35.805-13.14 61.531-48.62 64.473-86.527 1.851-24.844-4.192-51.273-20.883-70.328-15.934-18.652-39.238-28.926-62.559-34.774-1.25-23 9.586-45.722 26.77-60.68 9.543-8.605 21.386-14.09 33.633-17.6l.93-.321c35.058 16.812 64.847 44.539 83.827 78.578 16.262 28.98 24.743 62.375 23.903 95.64-.16 43.016-16.239 85.606-43.801 118.497-26.063 31.367-62.516 53.93-102.246 62.965-39.836 9.191-82.695 5.226-119.977-11.704-36.48-16.293-67.386-44.617-87.047-79.457-16.355-28.953-25.007-62.336-24.289-95.64.078-41.496 14.813-82.672 40.54-115.121 20.55-25.91 47.812-46.512 78.581-58.602zm0 0" fill="#e60000" />
              <path d="m228.809 20.402c13.03-2.203 26.421-3.71 39.586-1.71l1.89.32-1.265.48c-12.247 3.512-24.09 8.996-33.633 17.602-17.184 14.957-28.02 37.68-26.77 60.68 23.32 5.847 46.625 16.12 62.559 34.773 16.691 19.055 22.734 45.484 20.883 70.328-2.942 37.906-28.668 73.387-64.473 86.527-23.863 8.899-51.191 9.008-74.75-.96-24.45-10.075-44.734-29.594-56.258-53.395-11.183-22.684-14.242-48.824-10.941-73.746 4.988-35.172 22.652-68.047 47.86-92.824 26.288-24.739 59.921-41.391 95.312-48.075zm0 0" fill="#fff" />
            </svg>
          </div>
        );
      case 2:
        return (
          <div className="instapay-icon">
            <svg width="24" height="24" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
              <g>
                <path style={{ opacity: 0.917 }} fill="#502974" d="M 134.5,153.5 C 145.5,153.5 156.5,153.5 167.5,153.5C 164.506,164.982 161.006,176.315 157,187.5C 145.895,188.82 134.728,188.82 123.5,187.5C 127.574,176.277 131.241,164.944 134.5,153.5 Z" />
              </g>
              <g>
                <path style={{ opacity: 0.956 }} fill="#f38270" d="M 174.5,153.5 C 186.171,153.333 197.838,153.5 209.5,154C 221.7,174.704 233.7,195.538 245.5,216.5C 221.583,238.461 197.25,259.961 172.5,281C 160.787,281.187 149.121,281.687 137.5,282.5C 136.289,281.726 136.289,280.893 137.5,280C 162.13,259.039 186.463,237.706 210.5,216C 198.097,195.357 186.097,174.523 174.5,153.5 Z" />
              </g>
            </svg>
          </div>
        );
      case 3:
        return <FontAwesomeIcon icon={faUniversity} />;
      default:
        return <FontAwesomeIcon icon={faCreditCard} />;
    }
  };

  const getPaymentMethodDetails = (methodId) => {
    switch (methodId) {
      case 1:
        return "01069903556";
      case 2:
        return "mahmoud.ibrahim74@instapay";
      case 3:
        return "رقم الحساب: 100061518902";
      default:
        return "";
    }
  };
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className={`modal-content modal-popup ${mode === "register" ? "register-mode" : ""}`}
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="alert-header">
              <h2>{title}</h2>
              <button className="close-btn" onClick={onClose}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="alert-content">
              {mode === "register" ? (
                <>
                  {packageDetails && (
                    <div className="selected-package">
                      <div className="package-header">
                        <FontAwesomeIcon icon={faCheckCircle} className="package-icon" />
                        <h3>الباقة المختارة</h3>
                      </div>
                      <div className="package-info">
                        <h4 className="package-name">{packageDetails.name}</h4>
                        <div className="package-price">
                          <span className="price-amount">{packageDetails.price}</span>
                          <span className="price-currency">ج.م</span>
                          <span className="price-period">/ {packageDetails.period_name}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  <Formik
                    initialValues={{
                      fullName: '',
                      email: '',
                      phone: '',
                      paymentMethod: ''
                    }}
                    validationSchema={validationSchema}
                    onSubmit={handleRegisterSubmit}
                  >
                    {({ isSubmitting, errors, touched, values, setFieldValue }) => (
                      <Form className="register-form">
                        <div className="form-group">
                          <label className="form-label">الاسم بالكامل</label>
                          <div className="input-wrapper">
                            <FontAwesomeIcon icon={faUser} className="input-icon" />
                            <Field
                              type="text"
                              name="fullName"
                              placeholder="أدخل اسمك الكامل"
                              className={`form-control ${errors.fullName && touched.fullName ? 'error' : ''}`}
                            />
                          </div>
                          <ErrorMessage name="fullName" component="div" className="error-message" />
                        </div>

                        <div className="form-group">
                          <label className="form-label">البريد الإلكتروني</label>
                          <div className="input-wrapper">
                            <FontAwesomeIcon icon={faEnvelope} className="input-icon" />
                            <Field
                              type="email"
                              name="email"
                              placeholder="أدخل بريدك الإلكتروني"
                              className={`form-control ${errors.email && touched.email ? 'error' : ''}`}
                            />
                          </div>
                          <ErrorMessage name="email" component="div" className="error-message" />
                        </div>

                        <div className="form-group">
                          <label className="form-label">رقم الهاتف</label>
                          <div className="input-wrapper">
                            <FontAwesomeIcon icon={faPhone} className="input-icon" />
                            <Field
                              type="tel"
                              name="phone"
                              placeholder="01xxxxxxxxx"
                              className={`form-control ${errors.phone && touched.phone ? 'error' : ''}`}
                            />
                          </div>
                          <ErrorMessage name="phone" component="div" className="error-message" />
                        </div>

                        <div className="form-group">
                          <label className={`form-label payment-label ${errors.paymentMethod && touched.paymentMethod ? 'error' : ''}`}>
                            <FontAwesomeIcon icon={faCreditCard} />
                            <span>طريقة الدفع</span>
                          </label>
                          {loadingPaymentMethods ? (
                            <div className="payment-loading">
                              <FontAwesomeIcon icon={faSpinner} spin />
                              <span>جاري تحميل طرق الدفع...</span>
                            </div>
                          ) : (
                            <div className={`payment-methods-grid ${errors.paymentMethod && touched.paymentMethod ? 'error' : ''}`}>
                              {paymentMethods.map((method) => (
                                <label key={method.id} className="payment-option">
                                  <Field
                                    type="radio"
                                    name="paymentMethod"
                                    value={method.id}
                                    className="payment-radio"
                                    onClick={() => setFieldValue('paymentMethod', method.id)}
                                  />
                                  <div className={`payment-card ${values.paymentMethod == method.id ? 'selected' : ''}`}>
                                    <div className="payment-icon">
                                      {getPaymentMethodIcon(method.id)}
                                    </div>
                                    <div className="payment-info">
                                      <h4 className="payment-name">{method.name}</h4>
                                      <p className="payment-details">
                                        {getPaymentMethodDetails(method.id)}
                                      </p>
                                    </div>
                                    <div className={`payment-check ${values.paymentMethod == method.id ? 'selected' : ''}`}>
                                      <FontAwesomeIcon icon={faCheckCircle} />
                                    </div>
                                  </div>
                                </label>
                              ))}
                            </div>
                          )}
                          <ErrorMessage name="paymentMethod" component="div" className="error-message" />
                        </div>

                        <motion.button
                          type="submit"
                          className="submit-button"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          disabled={isSubmitting || submitting}
                        >
                          {submitting ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} spin />
                              <span>جاري التسجيل...</span>
                            </>
                          ) : (
                            <>
                              <span>سجّل الآن</span>
                              <FontAwesomeIcon icon={faPaperPlane} />
                            </>
                          )}
                        </motion.button>
                      </Form>
                    )}
                  </Formik>
                </>
              ) : (
                children
              )}
            </div>
            {/* إذا تم تمرير actions، اعرضها هنا */}
            {mode === "register" ? null : (
              actions ? (
                <div className="alert-actions custom-actions">
                  {Array.isArray(actions) ? actions.map((action, idx) => (
                    <React.Fragment key={idx}>{action}</React.Fragment>
                  )) : actions}
                </div>
              ) : (
                showModalButtons && (
                  <div className="alert-actions">
                    <button
                      className="cancel-btn"
                      onClick={onClose}
                      disabled={loading}
                    >
                      {cancelText}
                    </button>
                    {onConfirm && (
                      <button
                        className="confirm-btn"
                        onClick={onConfirm}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin />
                            جاري التنفيذ...
                          </>
                        ) : (
                          confirmText
                        )}
                      </button>
                    )}
                  </div>
                )
              )
            )}
          </motion.div>
        </motion.div>
      )}

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          backdrop-filter: blur(8px);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 2000;
          padding: 1rem;
        }

        .modal-content {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          border-radius: 24px;
          width: 100%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
          scrollbar-width: none; /* Firefox */
          -ms-overflow-style: none; /* IE 10+ */
          overscroll-behavior: contain;
          scroll-behavior: smooth;
        }

        .modal-content::-webkit-scrollbar {
          display: none;
        }

        .modal-popup {
          max-width: 500px;
        }

        .modal-popup.register-mode {
          max-width: 600px;
        }

        .alert-header {
          padding: 1.5rem;
          border-bottom: 2px solid rgba(226, 232, 240, 0.8);
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #e4e4f1;
          border-top-left-radius: 24px;
          border-top-right-radius: 24px;
        }

        .alert-header h2 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 600;
          color: #2d3748;
        }

        .close-btn {
          background: none;
          border: none;
          color: #64748b;
          cursor: pointer;
          padding: 0.5rem;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }

        .close-btn:hover {
          background: rgba(0, 0, 0, 0.05);
          color: #1e293b;
        }

        .alert-content {
          padding: 1.5rem;
        }

        .alert-content p {
          margin: 0 0 1rem 0;
          color: #4a5568;
        }

        .alert-actions {
          padding: 1.5rem;
          border-top: 2px solid rgba(226, 232, 240, 0.8);
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          background: #e4e4f1;
          border-bottom-left-radius: 24px;
          border-bottom-right-radius: 24px;
        }

        .alert-actions button {
          padding: 0.75rem 1.5rem;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .alert-actions .cancel-btn {
          background: #f1f5f9;
          color: #2d3748;
          border: none;
        }

        .alert-actions .confirm-btn {
          background: #3085d6;
          color: white;
          border: none;
        }

        .alert-actions button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .alert-actions.custom-actions {
          justify-content: flex-start;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        /* Registration Form Styles */
        .selected-package {
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          border: 2px solid rgba(102, 126, 234, 0.1);
          border-radius: 16px;
          padding: 20px;
          margin-bottom: 24px;
        }

        .package-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 12px;
        }

        .package-icon {
          color: #48bb78;
          font-size: 1.1rem;
        }

        .package-header h3 {
          margin: 0;
          font-size: 1rem;
          font-weight: 700;
          color: #2d3748;
        }

        .package-info {
          text-align: center;
        }

        .package-name {
          margin: 0 0 10px 0;
          font-size: 1.3rem;
          font-weight: 700;
          color: #667eea;
        }

        .package-price {
          display: flex;
          align-items: baseline;
          justify-content: center;
          gap: 6px;
        }

        .price-amount {
          font-size: 1.8rem;
          font-weight: 800;
          color: #2d3748;
        }

        .price-currency {
          font-size: 1.1rem;
          font-weight: 600;
          color: #718096;
        }

        .price-period {
          font-size: 0.95rem;
          color: #718096;
          font-weight: 500;
        }

        .register-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
        }

        .form-label {
          font-size: 0.95rem;
          font-weight: 700;
          color: #2d3748;
          margin-bottom: 8px;
          text-align: right;
        }

        .payment-label {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .input-wrapper {
          position: relative;
        }

        .input-icon {
          position: absolute;
          right: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #718096;
          font-size: 1rem;
          z-index: 2;
        }

        .form-control {
          width: 100%;
          padding: 14px 45px 14px 14px;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          font-size: 0.95rem;
          font-family: inherit;
          background: rgba(255, 255, 255, 0.8);
          color: #2d3748;
          transition: all 0.3s ease;
          box-sizing: border-box;
        }

        .form-control:focus {
          outline: none;
          border-color: #667eea;
          background: rgba(255, 255, 255, 0.95);
          box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
        }

        .form-control.error {
          border-color: #f56565;
          background: rgba(254, 242, 242, 0.8);
        }

        .error-message {
          color: #f56565;
          font-size: 0.8rem;
          margin-top: 6px;
          text-align: right;
          font-weight: 500;
        }

        .payment-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 24px;
          color: #718096;
          font-size: 0.95rem;
          background: rgba(255, 255, 255, 0.5);
          border-radius: 12px;
          border: 2px dashed #e2e8f0;
        }

        .payment-methods-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 12px;
        }

        .payment-methods-grid.error {
          border: 2px solid #f56565;
          border-radius: 12px;
          padding: 12px;
          background: rgba(254, 242, 242, 0.3);
        }

        .payment-option {
          display: block;
          cursor: pointer;
        }

        .payment-radio {
          display: none;
        }

        .payment-card {
          display: flex;
          align-items: center;
          padding: 16px;
          background: rgba(255, 255, 255, 0.8);
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          transition: all 0.3s ease;
          gap: 12px;
        }

        .payment-option:hover .payment-card {
          border-color: #667eea;
          background: rgba(255, 255, 255, 0.95);
          box-shadow: 0 4px 16px rgba(102, 126, 234, 0.1);
        }

        .payment-radio:checked + .payment-card,
        .payment-card.selected {
          border-color: #667eea;
          background: rgba(102, 126, 234, 0.05);
          box-shadow: 0 4px 16px rgba(102, 126, 234, 0.15);
        }

        .payment-icon {
          width: 45px;
          height: 45px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(102, 126, 234, 0.1);
          border-radius: 10px;
          color: #667eea;
          font-size: 1.2rem;
          flex-shrink: 0;
        }

        .vodafone-icon,
        .instapay-icon {
          width: 45px;
          height: 45px;
          background: #fff;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 5px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .vodafone-icon svg,
        .instapay-icon svg {
          width: 100%;
          height: 100%;
        }

        .payment-info {
          flex: 1;
          text-align: right;
        }

        .payment-name {
          font-size: 1rem;
          font-weight: 700;
          color: #2d3748;
          margin: 0 0 4px 0;
        }

        .payment-details {
          font-size: 0.85rem;
          color: #718096;
          font-weight: 500;
          margin: 0;
        }

        .payment-check {
          width: 24px;
          height: 24px;
          border: 2px solid #e2e8f0;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #fff;
          color: transparent;
          font-size: 0.8rem;
          transition: all 0.3s ease;
          flex-shrink: 0;
        }

        .payment-check svg {
          display: none;
        }

        .payment-check.selected {
          background: #667eea !important;
          color: #fff !important;
          border-color: #667eea !important;
        }

        .payment-check.selected svg {
          display: block !important;
          color: #fff !important;
        }

        .submit-button {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          border-radius: 12px;
          padding: 14px 28px;
          font-size: 1rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          margin-top: 10px;
        }

        .submit-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 12px 32px rgba(102, 126, 234, 0.4);
        }

        .submit-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .modal-content {
            max-width: 95%;
            margin: 1rem;
          }

          .modal-popup.register-mode {
            max-width: 95%;
          }

          .alert-header {
            padding: 1rem;
          }

          .alert-header h2 {
            font-size: 1.25rem;
          }

          .alert-content {
            padding: 1rem;
          }

          .alert-actions {
            padding: 1rem;
            flex-direction: column;
          }

          .alert-actions button {
            width: 100%;
          }

          .selected-package {
            padding: 16px;
          }

          .package-name {
            font-size: 1.2rem;
          }

          .price-amount {
            font-size: 1.6rem;
          }

          .form-control {
            padding: 12px 40px 12px 12px;
            font-size: 0.9rem;
          }

          .payment-card {
            padding: 14px;
            gap: 10px;
          }

          .payment-icon {
            width: 40px;
            height: 40px;
            font-size: 1.1rem;
          }

          .vodafone-icon,
          .instapay-icon {
            width: 40px;
            height: 40px;
            padding: 4px;
          }

          .payment-name {
            font-size: 0.95rem;
          }

          .payment-details {
            font-size: 0.8rem;
          }

          .submit-button {
            padding: 12px 24px;
            font-size: 0.95rem;
          }
        }

        @media (max-width: 480px) {
          .modal-content {
            max-width: 100%;
            margin: 0.5rem;
            border-radius: 16px;
          }

          .modal-popup.register-mode {
            max-width: 100%;
          }

          .alert-header {
            padding: 0.75rem;
          }

          .alert-header h2 {
            font-size: 1.1rem;
          }

          .alert-content {
            padding: 0.75rem;
          }

          .alert-actions {
            padding: 0.75rem;
          }

          .selected-package {
            padding: 12px;
          }

          .package-name {
            font-size: 1.1rem;
          }

          .price-amount {
            font-size: 1.4rem;
          }

          .form-control {
            padding: 10px 35px 10px 10px;
            font-size: 0.85rem;
          }

          .payment-card {
            padding: 12px;
            gap: 8px;
          }

          .payment-icon {
            width: 35px;
            height: 35px;
            font-size: 1rem;
          }

          .vodafone-icon,
          .instapay-icon {
            width: 35px;
            height: 35px;
            padding: 3px;
          }

          .payment-name {
            font-size: 0.9rem;
          }

          .payment-details {
            font-size: 0.75rem;
          }

          .submit-button {
            padding: 10px 20px;
            font-size: 0.9rem;
          }
        }
      `}</style>
    </AnimatePresence>
  );
};

export default ModalPopUp; 